import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Check, RefreshCw, Download, Puzzle, Settings, Loader2, ScanEye, Wand2, Target, Radar, Zap, Store, ArrowRight, Keyboard, GripVertical, Maximize2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../../components/ui/accordion';

const tones = ['Professional', 'Warm', 'Assertive', 'Concise', 'Confident', 'Apologetic', 'Friendly'];
const languages = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Japanese', 'Chinese', 'Korean'];

const LAYERS = [
  {
    name: 'PI Check', icon: ScanEye, color: '#8B5CF6', bg: 'bg-[#8B5CF6]/10', border: 'border-[#8B5CF6]/20',
    tagline: 'Know how your message lands before you send',
    desc: 'Scores every message 0-100 for emotional impact. Detects passive-aggression, guilt-tripping, escalation, and over-apologizing. Shows exactly how the receiver will feel.',
    features: ['Real-time PI Score', 'Conflict signals detection', '15+ emotional dimensions', 'Works offline (Stage 1 heuristics)'],
  },
  {
    name: 'PI Craft', icon: Wand2, color: '#06B6D4', bg: 'bg-[#06B6D4]/10', border: 'border-[#06B6D4]/20',
    tagline: 'Transform rough thoughts into precision AI casts',
    desc: 'Takes your raw idea and restructures it into an expert-level cast optimized for your chosen AI platform. Supports ChatGPT, Claude, Gemini, Perplexity, Copilot, Cursor, and Emergent.',
    features: ['Platform-specific formatting', '7 AI targets', 'Role-based context', 'Dev mode for coding casts'],
  },
  {
    name: 'PI Cast', icon: Target, color: '#F59E0B', bg: 'bg-amber-500/10', border: 'border-amber-500/20',
    tagline: 'Generate complete casts from a single outcome',
    desc: 'Describe the outcome you want and PI Cast generates a full, structured cast ready to paste into any AI. Supports 20+ categories from coding to marketing to legal.',
    features: ['Outcome-driven generation', '20+ categories', 'Tone & role customization', 'One-click copy to clipboard'],
  },
  {
    name: 'Token Optimizer', icon: Zap, color: '#10B981', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20',
    tagline: 'Same result, fewer tokens, lower cost',
    desc: 'Analyzes verbose casts and compresses them without losing meaning. Shows before/after token count and estimated cost savings. Essential for API-heavy workflows.',
    features: ['Token count reduction', 'Cost savings estimate', 'Meaning preservation', 'Platform-aware optimization'],
  },
  {
    name: 'PI Loop', icon: Radar, color: '#8B5CF6', bg: 'bg-[#8B5CF6]/10', border: 'border-[#8B5CF6]/20',
    tagline: 'Multi-pass refinement for maximum precision',
    desc: 'Runs your cast through multiple refinement passes. Each iteration improves clarity, specificity, and structure. The AI critiques and improves its own output.',
    features: ['3-pass refinement engine', 'Self-critique loop', 'Progressive improvement', 'Final score comparison'],
  },
  {
    name: 'PI Store', icon: Store, color: '#06B6D4', bg: 'bg-[#06B6D4]/10', border: 'border-[#06B6D4]/20',
    tagline: '4.3 million precision casts ready to use',
    desc: '75 domains, 30 task types, 6 platforms, 20 roles, 16 specifics — every combination pre-generated with platform-specific formatting. Browse, filter, copy.',
    features: ['4,320,000 casts', '75 domain packages', 'Hover preview', 'Instant copy-to-clipboard'],
  },
];

const EXT_FEATURES = [
  { icon: GripVertical, label: 'Drag & Resize', desc: 'Floating panel you can position anywhere on any webpage' },
  { icon: Keyboard, label: 'Slash Commands', desc: 'Type "/" in any text field for autocomplete cast commands' },
  { icon: Maximize2, label: 'Ctrl+Shift+P', desc: 'Global keyboard shortcut to toggle the panel on any site' },
];

export default function ExtensionPage() {
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [settings, setSettings] = useState({
    triggerSensitivity: 12, defaultTone: 'Professional', defaultLanguage: 'English', autoShowPopup: true,
  });

  const handleSync = async () => { setSyncing(true); await new Promise(r => setTimeout(r, 1500)); setSyncing(false); };

  return (
    <div className="space-y-8 max-w-5xl" data-testid="extension-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Chrome Extension</h1>
        <p className="text-white/55 text-sm">All 6 layers of SendPI intelligence — injected into every webpage you visit</p>
      </div>

      {/* Connection Status */}
      <div className="rounded-2xl p-6 border bg-emerald-500/5 border-emerald-500/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
              <Check className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-emerald-400 text-sm font-bold">Connected</span>
              </div>
              <p className="text-white/55 text-xs">Plan: {user?.plan?.toUpperCase() || 'FREE'} &middot; v1.0.0</p>
            </div>
          </div>
          <div className="flex gap-2">
            <a href="/sendpi-extension.zip" download>
              <Button className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full text-xs font-bold px-5" data-testid="download-ext-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download .zip
              </Button>
            </a>
            <Button onClick={handleSync} disabled={syncing} className="bg-white/5 hover:bg-white/10 text-white border border-white/10 rounded-full text-xs" data-testid="force-sync-btn">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Sync</>}
            </Button>
          </div>
        </div>
      </div>

      {/* What the Extension Does — All 6 Layers */}
      <div>
        <h2 className="text-white font-bold text-lg mb-4">What the extension does</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {LAYERS.map(l => (
            <div key={l.name} className={`rounded-xl border ${l.border} bg-[#0E1830] p-4 hover:bg-white/[0.02] transition-all`} data-testid={`layer-${l.name.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-2.5 mb-2">
                <div className={`w-8 h-8 rounded-lg ${l.bg} flex items-center justify-center`}>
                  <l.icon className="w-4 h-4" style={{ color: l.color }} />
                </div>
                <h3 className="text-white font-bold text-sm">{l.name}</h3>
              </div>
              <p className="text-white/70 text-[11px] font-medium mb-1.5">{l.tagline}</p>
              <p className="text-white/35 text-[11px] leading-relaxed mb-3">{l.desc}</p>
              <div className="flex flex-wrap gap-1">
                {l.features.map(f => (
                  <span key={f} className="text-[9px] px-2 py-0.5 rounded-full bg-white/[0.04] text-white/40 border border-white/[0.06] font-medium">{f}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Extension-specific features */}
      <div className="grid sm:grid-cols-3 gap-3">
        {EXT_FEATURES.map(f => (
          <div key={f.label} className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-4 text-center">
            <f.icon className="w-6 h-6 text-[#8B5CF6] mx-auto mb-2" />
            <h4 className="text-white font-bold text-xs mb-1">{f.label}</h4>
            <p className="text-white/35 text-[11px]">{f.desc}</p>
          </div>
        ))}
      </div>

      {/* Install Steps */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-bold text-sm mb-4">Quick Install</h3>
        <ol className="space-y-3">
          {[
            { step: '1', text: 'Download the extension zip above, or install from Chrome Web Store' },
            { step: '2', text: 'Go to chrome://extensions → Enable "Developer mode" → "Load unpacked" → select the extracted folder' },
            { step: '3', text: 'Click the SendPI icon in Chrome toolbar and sign in with your account' },
            { step: '4', text: 'Use Ctrl+Shift+P on any webpage to open the floating panel, or type "/" in any text field for slash commands' },
          ].map(s => (
            <li key={s.step} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#8B5CF6]/20 text-[#8B5CF6] flex items-center justify-center text-xs font-bold shrink-0">{s.step}</span>
              <p className="text-white/60 text-sm">{s.text}</p>
            </li>
          ))}
        </ol>
      </div>

      {/* Extension Settings */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Settings className="w-5 h-5 text-white/55" />
          <h3 className="text-white font-bold text-sm">Extension Settings</h3>
        </div>
        <div className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-white/70">Trigger sensitivity</Label>
              <span className="text-white/55 text-sm">{settings.triggerSensitivity} chars</span>
            </div>
            <Slider value={[settings.triggerSensitivity]} onValueChange={([v]) => setSettings(s => ({ ...s, triggerSensitivity: v }))} min={5} max={100} step={5} />
            <p className="text-white/40 text-xs">SendPI appears after you type this many characters</p>
          </div>
          <div className="space-y-2">
            <Label className="text-white/70">Default tone</Label>
            <Select value={settings.defaultTone} onValueChange={(v) => setSettings(s => ({ ...s, defaultTone: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0E1830] border-white/10">
                {tones.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-white/5">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-white/70">Default language</Label>
            <Select value={settings.defaultLanguage} onValueChange={(v) => setSettings(s => ({ ...s, defaultLanguage: v }))}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0E1830] border-white/10">
                {languages.map(l => <SelectItem key={l} value={l} className="text-white hover:bg-white/5">{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-white/70">Auto-show popup</Label>
              <p className="text-white/40 text-xs">Show SendPI when you start typing</p>
            </div>
            <Switch checked={settings.autoShowPopup} onCheckedChange={(v) => setSettings(s => ({ ...s, autoShowPopup: v }))} />
          </div>
          <Button className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white rounded-full text-xs font-bold" data-testid="save-settings-btn">Save settings</Button>
        </div>
      </div>

      <Accordion type="single" collapsible>
        <AccordionItem value="how" className="border-white/10 bg-[#0E1830] rounded-xl px-6">
          <AccordionTrigger className="text-white hover:text-white/80 text-sm">How the extension connects to your account</AccordionTrigger>
          <AccordionContent className="text-white/55 text-sm">
            When you sign in via the extension popup, SendPI generates a secure JWT token stored locally in Chrome. This token authenticates all API calls. Your message content is processed in real-time and never stored — only metadata (tone, score, timestamp) is saved for your Brain insights.
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
